import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  Fish,
  MapPin,
  Award,
  Calendar,
  Snowflake,
  ShieldCheck,
  Info,
} from 'lucide-react';
import PlatformFooter from '../../components/optimar/PlatformFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InfoLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4">
    <div className="mt-0.5 rounded-md bg-slate-100 p-2">
      <Icon className="h-4 w-4 text-slate-700" />
    </div>
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{value}</p>
    </div>
  </div>
);

/**
 * Public verification page for the Optimar-inspired demo batch.
 * Reached by scanning the QR on the customer view, which encodes
 *   ${origin}/verify/OPT-DEMO-001
 * No authentication is required (mirrors the existing public VerifyBatch).
 * Only limited customer-facing information is shown.
 */
const OptimarVerify = ({ batchId }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/optimar/batches/${batchId}/customer-view`
        );
        setView(res.data);
      } catch (err) {
        console.error(err);
        setError('Verification failed for this batch.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [batchId]);

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-slate-50"
        data-testid="optimar-verify-loading"
      >
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-slate-600">Verifying batch…</p>
        </div>
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center">
          <AlertTriangle className="mx-auto mb-3 h-12 w-12 text-rose-500" />
          <h1 className="text-xl font-bold text-slate-900">Verification failed</h1>
          <p className="mt-2 text-sm text-slate-600">{error || 'Batch not found'}</p>
        </div>
      </div>
    );
  }

  const productionDate = new Date(view.production_date).toLocaleDateString('en-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const resolved = !!view.traceability_resolved;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="optimar-verify-page">
      {/* Header bar (same visual language as the PureSustain VerifyBatch page) */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-6 text-white sm:py-8">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 sm:px-6">
          <Shield className="h-8 w-8 shrink-0 sm:h-10 sm:w-10" />
          <div className="min-w-0">
            <h1
              className="text-2xl font-bold leading-tight sm:text-3xl"
              style={{ fontFamily: 'Manrope' }}
            >
              Verified by PureSustain
            </h1>
            <p className="text-xs text-blue-100 sm:text-sm">
              Supply Chain Transparency
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-5 px-4 py-6 sm:space-y-6 sm:px-6 sm:py-10">
        {/* Public demo banner */}
        <div
          data-testid="optimar-verify-public-banner"
          className="rounded-full border border-blue-300 bg-blue-50 px-4 py-2 text-center text-xs font-medium text-blue-800"
        >
          Public demo verification view — mock data only.
        </div>

        {/* Demo data disclaimer */}
        <div
          data-testid="optimar-verify-demo-banner"
          className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
        >
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="text-sm leading-relaxed">
            <strong className="font-semibold">Optimar-inspired demo data.</strong>{' '}
            This view does not represent real Optimar customer, plant, SCADA or
            production data.
          </div>
        </div>

        {/* Product header card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-mono text-xs text-slate-500">
                Batch {view.batch_id}
              </p>
              <h2
                className="mt-1 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl"
                style={{ fontFamily: 'Manrope' }}
              >
                {view.product}
              </h2>
              <p className="mt-1 max-w-lg text-sm text-slate-600">
                {view.customer_message}
              </p>
            </div>
            <span
              data-testid="optimar-verify-traceability-status"
              className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium sm:text-sm ${
                resolved
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-amber-300 bg-amber-50 text-amber-800'
              }`}
            >
              {resolved ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              {view.traceability_status}
            </span>
          </div>
        </div>

        {/* Limited customer-facing fields */}
        <div
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          data-testid="optimar-verify-customer-fields"
        >
          <InfoLine icon={Fish} label="Product" value={view.product} />
          <InfoLine icon={MapPin} label="Origin" value={view.origin} />
          <InfoLine icon={Award} label="Quality grade" value={view.quality_grade} />
          <InfoLine icon={Calendar} label="Production date" value={productionDate} />
          <InfoLine icon={Snowflake} label="Storage status" value={view.storage_status} />
          <InfoLine
            icon={ShieldCheck}
            label="Sustainability / compliance"
            value={view.sustainability_badges.join(' · ')}
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs leading-relaxed text-slate-600">
          Only a limited subset of batch information is exposed on this public
          verification view. Full traceability (RFID linkage, SCADA stage
          events, operator data, manual corrections) remains available to
          auditors only.
        </div>

        <div className="border-t border-slate-200 pt-6 text-center">
          <p className="text-xs text-slate-400">
            Generated by PureSustain for traceability and reporting support.
            This page does not replace regulatory filings or accredited
            certification.
          </p>
        </div>

        <PlatformFooter variant="card" />
      </div>
    </div>
  );
};

export default OptimarVerify;
